"use client";

import AnimatedLogoMark from "@/components/brand/AnimatedLogoMark";
import { useState, useEffect, useRef, Suspense } from "react";
import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";
import Link from "@/lib/localized-link";
import { useLocalizedRouter } from "@/lib/localized-router";
import { analyzeURL, intakeAnalyze, API_BASE, RateLimitError, UserInputError, NetworkError } from "@/lib/api";
import {
  canonicalListingKey,
  extractListingUrl,
  SUPPORTED_LISTING_DOMAINS,
  looksLikeLink,
} from "@/lib/listing-url";
import { trackEvent } from "@/lib/tracking";
import { useLocale, useT } from "@/lib/locale-context";
import { useAuth } from "@/lib/auth-context";
import type { AnalysisResult, IntakeAnalyzeResponse, IntakeHints } from "@/types";
import type { TranslationKey } from "@/lib/i18n/keys";
import { formatPrice } from "@/lib/api";
import { formatMonthDay } from "@/lib/blog-format";
import Illo from "@/components/Illo";
import {
  INTAKE_ART,
  INTAKE_FIELD_LABEL,
  calculatorHref,
  excerpt,
  intakeCase,
  listedAtMs,
  listingsHref,
  pricePerM2,
  unknownParts,
  unreadFields,
} from "@/lib/intake-outcome";
import TrackHomeCta from "@/components/TrackHomeCta";
import { captureError } from "@/lib/sentry";
import { captureEvent } from "@/lib/tracking";
import MobileBottomChromeOffset from "@/components/MobileBottomChromeOffset";
import RateLimitBanner from "@/components/RateLimitBanner";
import TrialOfferCard from "@/components/TrialOfferCard";
import PasteFromClipboard from "@/components/PasteFromClipboard";
import UrlPreviewCard from "@/components/UrlPreviewCard";
import RecentActivity from "@/components/RecentActivity";
import AnalyzeLanding from "@/components/AnalyzeLanding";
import { recordAnalyze } from "@/lib/recent-activity";
import { useAuthHref } from "@/lib/use-return-to";
// Skeleton is bundled inline (not dynamic) so it appears the moment
// the user lands on /analyze with ?url=, no extra chunk fetch. The
// component is small enough that the bundle cost is negligible, and
// having it instantly available eliminates the dead-air gap where
// neither the skeleton nor the result card had finished loading.
import ResultCardSkeleton from "@/components/ResultCardSkeleton";
import AnalysisProgress from "@/components/AnalysisProgress";
import { Check, ExternalLink, ImagePlus, X } from "lucide-react";
import { pickPhoto } from "@/lib/photo-pick";
import { IntakeImageError, prepareIntakeImage } from "@/lib/intake-image";
import { useReportAnalysisBusy, useReportResultShown } from "@/lib/analysis-busy";
import { useHoldInterruptions } from "@/lib/floating-slot";

// Persisted-result storage for anonymous users. Signed-in users get
// permalinks via /h?id=N so a locale toggle (which calls
// router.replace and remounts the [locale]/analyze tree) preserves
// their result via the URL. Anonymous users have no permalink, so a
// locale toggle wiped the in-memory ``result`` state and dropped them
// back on the empty input page. localStorage round-trip restores the
// most recent anon result on remount.
const ANON_RESULT_STORAGE_KEY = "menzil-anon-analyze-result";
// 1 hour - long enough to survive a few minutes of tab churn / locale
// toggles, short enough that returning users tomorrow see a fresh
// page rather than a stale verdict.
const ANON_RESULT_TTL_MS = 60 * 60 * 1000;

interface PersistedAnonResult {
  result: AnalysisResult;
  ts: number;
}

function loadPersistedAnonResult(): AnalysisResult | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(ANON_RESULT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedAnonResult;
    if (!parsed?.result || typeof parsed.ts !== "number") return null;
    if (Date.now() - parsed.ts > ANON_RESULT_TTL_MS) {
      window.localStorage.removeItem(ANON_RESULT_STORAGE_KEY);
      return null;
    }
    return parsed.result;
  } catch {
    return null;
  }
}

function savePersistedAnonResult(result: AnalysisResult) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      ANON_RESULT_STORAGE_KEY,
      JSON.stringify({ result, ts: Date.now() } satisfies PersistedAnonResult),
    );
  } catch {
    /* quota exceeded / disabled storage - non-fatal */
  }
}

function clearPersistedAnonResult() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(ANON_RESULT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

// ResultCard (~700 LoC, pulls the streamed analysis UI, SHAP panel,
// findings chips, narrative panel) and its skeleton are only rendered
// AFTER the user submits a URL. Keeping them in the initial /analyze
// bundle dragged ~90 KB of JS onto a page whose first paint doesn't
// need any of it. Dynamic-importing here: user lands on /analyze, sees
// the hero input instantly; the chunk starts fetching once analyzeURL
// is invoked, so the skeleton shows without a user-visible delay.
// ssr:false because the result is always user-driven.
// ``loading`` keeps the skeleton mounted while the ResultCard chunk
// streams in, so the user never sees a flash of empty space between
// "API resolved" and "ResultCard rendered". The standalone skeleton
// block below covers the earlier "API still loading" phase. Together
// the two phases hand off without layout shift.
const PartialResultCard = dynamic(() => import("@/components/PartialResultCard"), {
  ssr: false,
});
const ResultCard = dynamic(() => import("@/components/ResultCard"), {
  ssr: false,
  loading: () => (
    <div className="max-w-6xl mx-auto mt-3 sm:mt-8">
      <ResultCardSkeleton />
    </div>
  ),
});

function AnalyzeContent() {
  const t = useT();
  const { tokens, refreshTokens, user, loading: authLoading } = useAuth();
  const searchParams = useSearchParams();
  const router = useLocalizedRouter();
  const registerHref = useAuthHref("register");
  const loginHref = useAuthHref("login");
  // No "· 1" cost chip on the Analyze button any more: an analysis costs
  // the person nothing on any account (owner, 2026-10-18).
  const [url, setUrl] = useState("");
  // Intent input temporarily removed; kept as an empty constant so the
  // submit payload + tracking stay intact for easy restore.
  const [userIntent] = useState("");
  const [loading, setLoading] = useState(false);
  useReportAnalysisBusy(loading);
  // An analysis on its way is the result arriving: no offer comes up
  // over it, only to be taken back when the result lands (ResultCard
  // holds from then on; lib/floating-slot).
  useHoldInterruptions(loading);
  const [error, setError] = useState("");
  // The message of a refusal (the backend read the listing but values no
  // such listing: X-Refusal-Reason): its card says "we don't value this
  // listing" and offers no retry. Compared with `error`, so any later
  // error of another kind falls back to the usual heading by itself.
  const [refusedMsg, setRefusedMsg] = useState<string | null>(null);
  // Its reason code (X-Refusal-Reason), which picks the card's next step.
  const [refusedReason, setRefusedReason] = useState<string | null>(null);
  // Inline error for any URL that isn't a supported bina.az/emlak.az
  // listing. Two variants:
  //   - `unsupportedHost = "tap.az"` etc. → tailored "tap.az isn't
  //      supported yet…" message naming the site.
  //   - `urlInvalid = true`              → generic "this isn't a
  //      bina.az/emlak.az URL…" message for random URLs.
  // Both render with an inline /calculate link so the user always has
  // a clear next step instead of getting an opaque backend toast.
  const [urlInvalid, setUrlInvalid] = useState(false);
  // The intake outcome for any link that is not a listing site, or for
  // pasted text: the fields read and, for a complete sale flat, the
  // valuation. "none" = the page held no listing.
  const [intake, setIntake] = useState<IntakeOutcome | null>(null);
  // Which kind of work the loading state shows.
  const [pending, setPending] = useState<"analyze" | "intake">("analyze");
  // A screenshot of a post (pasted, dropped or picked): the intake reads
  // it with its vision pass. One at a time, as the data URL the API takes.
  const [shot, setShot] = useState<{ dataUrl: string; name: string } | null>(null);
  const [shotError, setShotError] = useState<"unsupported" | "too_big" | "unreadable" | null>(null);
  const [shotBusy, setShotBusy] = useState(false);
  // What the last read was run on. While the box (and the screenshot)
  // still hold exactly that, the submit button reads as done instead of
  // offering the same read again (owner, 2026-10-25); any edit re-arms it.
  const [submitted, setSubmitted] = useState<{ text: string; shot: string | null } | null>(null);
  // The taps the card asked for when the post did not say (sale or rent,
  // flat or house), kept across re-reads of the same input.
  const [hints, setHints] = useState<IntakeHints | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [rateLimitError, setRateLimitError] = useState<RateLimitError | null>(null);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  // Nothing above the answer while a result is on screen (owner decision
  // 1, 2026-10-29): the pill row in the shell steps aside, and the link
  // form and its phone bar wait for "Başqa elanı qiymətləndirin" at the
  // end of the result.
  useReportResultShown(Boolean(result));
  const [autoTriggered, setAutoTriggered] = useState(false);
  // Synchronous re-entry guard. ``setLoading(true)`` only disables
  // the submit button after React commits the next render - on slow
  // mobile devices that's a 50-200 ms window, long enough for a fast
  // double-tap (or a panicked re-tap after an error toast appears) to
  // fire handleSubmit twice and POST /api/analyze twice. Backend
  // rate-limit churn data showed 16 % of analyse rows had a duplicate
  // within 5 s for the same fingerprint + URL, including 6 % under
  // 600 ms which is below human tap reaction time. The ref flips
  // synchronously inside the handler so the second tap sees `true`
  // and bails before any state has had a chance to update.
  const submittingRef = useRef(false);
  // Held so the X-clear button in the URL input can wipe the value
  // AND restore focus in a single tap (so the user can paste again
  // without re-targeting the field).
  const urlInputRef = useRef<HTMLInputElement>(null);

  // When an analysis runs, pull the result area to the top so the user
  // sees the result (and its skeleton) from the top instead of being
  // stranded mid-page. Keyed on `loading` (only true during a live
  // submit), so a restored/cached result on mount never auto-scrolls.
  // scroll-mt on the wrapper keeps it clear of the sticky header.
  const resultRef = useRef<HTMLDivElement | null>(null);
  // Tracks an in-flight run so the scroll-to-top below only fires for
  // a FRESH result - a restored/cached result on mount must not yank
  // the scroll position.
  const ranRef = useRef(false);
  useEffect(() => {
    if (loading) {
      ranRef.current = true;
      resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (ranRef.current && result) {
      // Fresh analysis just landed: bring the user to the top of the
      // page so the result reads from its beginning instead of from
      // wherever the loading skeleton left the viewport. INSTANT jump:
      // a smooth scroll gets cancelled mid-flight by the huge
      // skeleton->result reflow (images/charts mounting), which left
      // users stranded mid-page.
      ranRef.current = false;
      // Triple pin - see /calculate: late reflows from mounting images
      // and charts can drag the viewport back after the first jump.
      window.scrollTo({ top: 0, behavior: "auto" });
      requestAnimationFrame(() => window.scrollTo({ top: 0 }));
      setTimeout(() => window.scrollTo({ top: 0 }), 150);
    }
  }, [loading, result]);

  // Auto-fill and auto-submit from query param.
  //
  // Sources:
  //   ?url=…  - direct (browser extension, manual links, our own
  //             /h redirects).
  //   ?text=… - the Web Share Target spec routes a plain-text share
  //             into the `text` field when the sharing app didn't
  //             provide an explicit URL. Android's share sheet does
  //             this when users long-press a bina.az post and pick
  //             "Share via text", or when a chat app sends the link
  //             as body text. Extract the first supported listing URL.
  // Any link that is not a listing site, or pasted text, through the
  // intake. The valuation (a complete sale flat) spends a token like a
  // manual calculation; a partial read costs nothing.
  async function runIntake(input: string, via: "typed" | "deeplink" | "hint", images?: string[], tapped?: IntakeHints | null) {
    if (submittingRef.current) return;
    const asLink = !images && looksLikeLink(input);
    submittingRef.current = true;
    setUrlInvalid(false);
    setPending("intake");
    setLoading(true);
    setError("");
    setRateLimitError(null);
    setResult(null);
    setIntake(null);
    setSubmitted({ text: input.trim(), shot: images?.[0] ?? null });
    setHints(tapped ?? null);
    trackEvent("intake.submit", { kind: images ? "image" : asLink ? "link" : "text", via });
    let navigating = false;
    try {
      const data = await intakeAnalyze({
        ...(images ? { images, text: input || undefined } : asLink ? { url: input } : { text: input }),
        ...(tapped ?? {}),
      });
      trackEvent("intake.result", { status: data.status, source: data.intake.source.kind });
      const v = data.status === "valued" ? data.valuation : null;
      if (v && v.verdict) {
        // A complete read is valued the way a listing site's link is:
        // the result itself, with the link, the photos and the seller's
        // words, and no read card before it (owner, 2026-10-27).
        refreshTokens();
        if (v.history_id) {
          // Signed in: the permalink, as for a listing site's link; the
          // reading view stays up through the navigation.
          navigating = true;
          router.replace(`/h?id=${v.history_id}`, { scroll: false });
          return;
        }
        setResult(v);
        savePersistedAnonResult(v);
        recordAnalyze(v, asLink ? input : null);
        return;
      }
      setIntake({ kind: "read", data });
    } catch (err) {
      if (err instanceof RateLimitError) {
        setRateLimitError(err);
      } else if (err instanceof UserInputError && err.status === 422) {
        setIntake({ kind: "none" });
        trackEvent("intake.result", { status: "none", source: images ? "image" : asLink ? "link" : "text" });
      } else if (err instanceof UserInputError) {
        setError(err.message);
      } else if (err instanceof NetworkError) {
        setError(t("error.network"));
      } else {
        setError(err instanceof Error ? err.message : "Intake failed");
        captureError(err, { url: input, source: "intake" });
      }
    } finally {
      if (!navigating) {
        setLoading(false);
        setPending("analyze");
      }
      submittingRef.current = false;
    }
  }

  useEffect(() => {
    const paramUrl =
      searchParams.get("url") || extractListingUrl(searchParams.get("text"));
    if (paramUrl && !autoTriggered) {
      if (submittingRef.current) return;

      // A deep link that is not one of the listing sites (the home
      // box, a share sheet, the extension) is read by the intake.
      if (canonicalListingKey(paramUrl) == null) {
        setUrl(paramUrl);
        setAutoTriggered(true);
        router.replace("/analyze", { scroll: false });
        void runIntake(paramUrl, "deeplink");
        return;
      }

      // Cache-reopen short-circuit: if we have a fresh anon result
      // for this exact URL in localStorage, hydrate it directly and
      // skip the API round-trip. ?fresh=1 forces a re-analyse and
      // bypasses the cache (matches the backend's fresh path). The
      // URL is replaced to /analyze?reopen=1 so PostHog auto-pageview
      // captures cache hits as a distinct surface, separate from the
      // /analyze?url= path that fired analyze_submitted. Signed-in
      // users skip this branch (they have permalinks via /h?id=N).
      const isForceFresh = searchParams.get("fresh") === "1";
      if (!user && !isForceFresh) {
        const cached = loadPersistedAnonResult();
        if (cached?.url && cached.url.trim() === paramUrl.trim()) {
          setUrl(paramUrl);
          setAutoTriggered(true);
          setResult(cached);
          setError("");
          setRateLimitError(null);
          setLoading(false);
          captureEvent("analyze_cached_view", {
            url: cached.url,
            verdict: cached.verdict,
          });
          router.replace("/analyze?reopen=1", { scroll: false });
          return;
        }
      }

      submittingRef.current = true;
      setUrl(paramUrl);
      setAutoTriggered(true);
      // Auto-submit
      (async () => {
        setLoading(true);
        setError("");
        setRateLimitError(null);
        setResult(null);
        try {
          const data = await analyzeURL(paramUrl.trim(), { fresh: searchParams.get("fresh") === "1" });
          // For signed-in users we promote the analysis to a permalink
          // immediately - the URL becomes /analyze/{historyId}, the
          // listing URL drops out of the address bar, and the result
          // page is shareable via that ID. Anonymous analyses don't get
          // a history row, so they fall through to the inline view.
          if (data.history_id) {
            // Logged-in: promote to permalink (drops ?url= naturally).
            // Keep loading=true through the navigation - same flicker
            // fix as the manual handleSubmit path.
            router.replace(`/h?id=${data.history_id}`, { scroll: false });
            return;
          }
          // Anonymous: strip the ?url= so the listing URL doesn't sit in
          // the address bar after the inline result renders. Same intent
          // as the manual-submit path's strip below.
          router.replace("/analyze", { scroll: false });
          setResult(data);
          if (data.property_type === "land") trackEvent("analyze.land", { listing_id: data.listing_id ?? null });
          savePersistedAnonResult(data);
          recordAnalyze(data, paramUrl);
          refreshTokens();
          setLoading(false);
        } catch (err) {
          router.replace("/analyze", { scroll: false });
          if (err instanceof RateLimitError) {
            // Expected: user out of quota. Banner handles the UX;
            // nothing to log - rate limits are normal product behaviour.
            setRateLimitError(err);
          } else if (err instanceof UserInputError) {
            // Expected: bad URL / invalid listing. Show the backend
            // message, don't treat as a bug.
            setError(err.message);
            setRefusedMsg(err.reason ? err.message : null);
            setRefusedReason(err.reason ?? null);
          } else if (err instanceof NetworkError) {
            // fetch() failed twice (we already retry once). Could be
            setError(t("error.network"));
          } else {
            const msg = err instanceof Error ? err.message : "Analysis failed";
            setError(msg);
            captureError(err, { url: paramUrl, source: "auto-analyze" });
          }
          setLoading(false);
        } finally {
          submittingRef.current = false;
        }
      })();
    }
  }, [searchParams, autoTriggered]);

  // Hydrate the most recent anon result from localStorage on mount.
  // This is what makes the result survive a locale toggle - the
  // toggle calls router.replace which remounts the [locale]/analyze
  // tree, wiping in-memory state. Signed-in users skip this because
  // their results live behind /h?id=N permalinks. Skip when an auto-
  // trigger is mid-flight (a fresh ?url= analyse is more relevant
  // than a stale cached one) or when we already have a result.
  useEffect(() => {
    if (authLoading) return;
    if (user) return;
    if (result) return;
    if (loading || autoTriggered) return;
    if (searchParams.get("url") || searchParams.get("text")) return;
    const stored = loadPersistedAnonResult();
    if (stored) setResult(stored);
    // We only want to hydrate once after auth resolves; the deps
    // capture the gates above. ESLint nags on missing `result` in
    // deps but adding it would re-run on every successful analyse
    // and stomp on freshly-set state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, autoTriggered, searchParams]);

  // No demo link is written into the box any more: the placeholder is
  // the example, the buyer pastes without clearing a stranger's URL
  // first (owner, 2026-10-18).

  /** A screenshot that arrived by paste, drop or the picker: redrawn to
   *  the size the intake takes, then shown above the box until it is sent
   *  or removed. True when the files were about a picture at all. */
  async function takeShot(files: ArrayLike<File> | null | undefined): Promise<boolean> {
    const { file, unsupported } = pickPhoto(files);
    if (!file) {
      if (unsupported) setShotError("unsupported");
      return unsupported;
    }
    setShotBusy(true);
    setShotError(null);
    try {
      const dataUrl = await prepareIntakeImage(file);
      setShot({ dataUrl, name: file.name || "" });
      setUrlInvalid(false);
      setIntake(null);
      setError("");
    } catch (e) {
      setShotError(e instanceof IntakeImageError ? e.code : "unreadable");
    } finally {
      setShotBusy(false);
    }
    return true;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = url.trim();
    if (shot) {
      // A screenshot goes through the intake whatever the box holds; the
      // text, when there is any, is read together with it.
      if (searchParams.get("url")) router.replace("/analyze", { scroll: false });
      await runIntake(trimmed, "typed", [shot.dataUrl]);
      return;
    }
    if (!trimmed) return;
    if (submittingRef.current) return;
    // Not one of the listing sites: any other link, or a pasted post's
    // text, goes through the intake (owner, 2026-10-16: "accepting any
    // link and trying to extract the values"). Only a short non-link
    // is a dead end.
    if (canonicalListingKey(trimmed) == null) {
      if (looksLikeLink(trimmed) || trimmed.length >= 40) {
        if (searchParams.get("url")) router.replace("/analyze", { scroll: false });
        await runIntake(trimmed, "typed");
        return;
      }
      setUrlInvalid(true);
      setError("");
      setRateLimitError(null);
      setResult(null);
      setIntake(null);
      return;
    }
    setUrlInvalid(false);
    submittingRef.current = true;

    // ``via``: every submit is the buyer's own link now (the demo
    // prefill is gone); the field stays so the event's shape does.
    const via = "typed";

    trackEvent("analyze.submit", {
      has_intent: !!userIntent,
      fresh: searchParams.get("fresh") === "1",
      via,
    });
    captureEvent("analyze_submitted", {
      has_intent: !!userIntent,
      fresh: searchParams.get("fresh") === "1",
      via,
    });

    setPending("analyze");
    setLoading(true);
    setError("");
    setRateLimitError(null);
    setResult(null);
    setIntake(null);

    // Strip any ?url=... query param left from the initial auto-trigger -
    // we don't want the listing URL reflected in the browser address bar.
    if (searchParams.get("url")) {
      router.replace("/analyze", { scroll: false });
    }

    try {
      const data = await analyzeURL(trimmed, {
        fresh: searchParams.get("fresh") === "1",
        userIntent,
      });
      // Fire for BOTH paths: signed-in analyses navigate to /h below and
      // used to skip this, so the event undercounted results by the whole
      // signed-in share (checklist "measurement gaps", 2026-09-19).
      captureEvent("analyze_result_received", {
        verdict: data.verdict,
        overpriced_pct: data.overpriced_pct,
        source: data.source,
        via,
        signed_in: Boolean(data.history_id),
      });
      if (data.history_id) {
        // Hold `loading` true through navigation - see /calculate for
        // the same fix. Without this the form briefly re-renders empty
        // between the API resolving and Next finishing the route change.
        router.replace(`/h?id=${data.history_id}`, { scroll: false });
        refreshTokens();
        return;
      }
      setResult(data);
      savePersistedAnonResult(data);
      recordAnalyze(data, trimmed);
      refreshTokens();
      setLoading(false);
    } catch (err) {
      if (err instanceof RateLimitError) {
        setRateLimitError(err);
      } else if (err instanceof UserInputError) {
        setError(err.message);
        setRefusedMsg(err.reason ? err.message : null);
        setRefusedReason(err.reason ?? null);
      } else if (err instanceof NetworkError) {
        setError(t("error.network"));
      } else {
        const msg = err instanceof Error ? err.message : "Analysis failed";
        setError(msg);
        captureError(err, { url, source: "manual-analyze" });
      }
      setLoading(false);
    } finally {
      submittingRef.current = false;
    }
  };

  const handleReset = () => {
    setUrl("");
    setResult(null);
    setIntake(null);
    setError("");
    setRateLimitError(null);
    setAutoTriggered(false);
    clearPersistedAnonResult();
    router.replace("/analyze", { scroll: false });
  };

  // "Başqa elanı qiymətləndirin" at the end of a result: the empty form
  // again, from its top, with the cursor in the box.
  const evaluateAnother = () => {
    handleReset();
    window.scrollTo({ top: 0 });
    requestAnimationFrame(() => urlInputRef.current?.focus({ preventScroll: true }));
  };

  // Compact form mode once a result or loading state exists - the
  // page pivots from "input is the star" to "result is the star", and
  // the form shrinks to a header strip so the user can still paste a
  // new URL without the hero taking a third of the viewport.
  const hasOutcome = loading || Boolean(result) || Boolean(intake) || Boolean(error) || Boolean(rateLimitError);
  // The box still holds what the card below was read from: the submit
  // button shows a check and "read" until the text or the screenshot
  // changes. Loading keeps its own look.
  const done = Boolean(intake) && !loading && submitted != null
    && url.trim() === submitted.text && (shot?.dataUrl ?? null) === submitted.shot;
  const startAnother = () => {
    setUrl("");
    setShot(null);
    setShotError(null);
    urlInputRef.current?.focus();
  };

  // Any inline "we couldn't read this link" outcome (backend error,
  // unsupported host, or a non-listing URL). While one is showing, the
  // error card below owns the URL input (Round 10d: the URL stays
  // editable in place, next to the retry action), so the main form and
  // the mobile sticky submit hide to avoid two competing inputs.
  const hasInlineError = Boolean(error) || urlInvalid;
  const refused = !urlInvalid && refusedMsg !== null && refusedMsg === error;
  const nextStep = refusalNextStep(refused ? refusedReason : null);
  const supportedSites = SUPPORTED_LISTING_DOMAINS.join(", ");

  return (
    // Generous top padding only in the hero state; once an outcome
    // exists (loading or result) the island/result sits close to the
    // navbar with a compact, consistent top gap (pt-6), matching
    // /calculate + /compare - the big py-16 gap above the loading
    // island read as a layout glitch that "snapped" smaller at the end.
    <main className="mx-auto px-4 sm:px-6 lg:px-8 pb-8 sm:pb-12 lg:pb-16">
      {/* ── Input surface ─────────────────────────────────────────────
          Generous max-w-3xl centered column in the "hero" state; compact
          strip once an outcome exists. The width of the form doesn't
          change on result arrival (avoids the jarring `max-w-4xl` →
          `max-w-7xl` animation the previous version had); instead the
          RESULT container below renders at its own wider width. */}
      <div className="max-w-3xl mx-auto">
        {/* The same heading block as /analyst and the calculators. */}
        {!hasOutcome && (
          <div className="mb-8 max-w-2xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
              {t("analyze.title")}
            </h1>
            <p className="mt-3 text-base text-gray-600 leading-relaxed">
              {t("analyze.subtitle")}
            </p>
          </div>
        )}

        {/* Hide the input form entirely for anonymous users once an
            outcome exists. They get one shot per fingerprint anyway,
            so leaving the URL bar in compact mode just dangles a CTA
            they can't actually use - and a price-tag-feeling chip
            after the result. Signed-in users keep the compact strip
            over a read card or an error, never over a result: nothing
            sits above the answer, and "Başqa elanı qiymətləndirin"
            at its end brings the form back (owner decision 1,
            2026-10-29).
            suppressHydrationWarning: password-manager extensions
            (Dashlane, 1Password, LastPass, Bitwarden) inject
            `data-*` attributes into the form + input between SSR and
            hydration. It's not a real mismatch - it's a third-party
            DOM walker - but React prints a noisy warning. The flag
            tells React to ignore attribute-level diffs here; the
            rendered output we control still gets reconciled normally. */}
        {/* Hidden while LOADING too (owner call): once the analysis is
            running there is nothing to type - the input card floating
            above the progress overlay was one layer too many. It
            returns with the result for the paste-next-URL flow. */}
        {/* A device that signed in before has no anonymous quota; say so
            before the paste, with the account when we know it (owner,
            2026-09-20). */}
        {!user && tokens?.account_required && !loading && !result && (
          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3">
            <div className="min-w-0 flex-1 text-sm text-gray-800">
              <span className="font-semibold text-gray-900">{t("rate.account_required.pre.title")}</span>{" "}
              {tokens.email_hint
                ? t("rate.account_required.hint").replace("{email}", tokens.email_hint)
                : t("rate.account_required.pre.body")}
            </div>
            <Link
              href={loginHref}
              className="inline-flex shrink-0 items-center justify-center rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700"
            >
              {t("auth.signin")}
            </Link>
          </div>
        )}
        {!(hasOutcome && !user) && !loading && !result && (
        <form
          onSubmit={handleSubmit}
          className={hasOutcome ? "mb-6 sm:mb-8" : "mb-8 sm:mb-10"}
          suppressHydrationWarning
        >
          {/* Soft card wrapper gives the form presence without shouting.
              In the hero state it's the page's focal point; in the
              compact post-result state it still reads as "the input"
              and not a banner. */}
          <div
            className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-5 space-y-3"
            onDragOver={(e) => { if (Array.from(e.dataTransfer?.types ?? []).includes("Files")) e.preventDefault(); }}
            onDrop={(e) => { if (e.dataTransfer?.files?.length) { e.preventDefault(); void takeShot(e.dataTransfer.files); } }}
          >
            {/* Live URL preview - shown above the input as soon as
                the pasted / typed URL resolves on the backend.
                Self-hides for empty / unrecognised input so the
                form layout collapses cleanly. */}
            <UrlPreviewCard url={url} variant="form" />
            {shot && (
              <div className="flex items-center gap-3 rounded-2xl border border-gray-200 p-3 sm:p-3.5">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={shot.dataUrl} alt="" className="h-14 w-14 shrink-0 rounded-lg bg-gray-100 object-cover sm:h-16 sm:w-16" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-gray-900">{t("intake.image.chip")}</div>
                  <div className="truncate text-xs text-gray-500">{shot.name || t("intake.image.hint")}</div>
                </div>
                <button
                  type="button"
                  onClick={() => { setShot(null); setShotError(null); }}
                  aria-label={t("intake.image.remove")}
                  title={t("intake.image.remove")}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            )}
            {shotError && (
              <p className="text-sm text-rose-600" role="alert">
                {t(shotError === "unsupported" ? "intake.image.unsupported" : shotError === "too_big" ? "intake.image.too_big" : "intake.image.unreadable")}
              </p>
            )}
            {/* Desktop: URL field + submit button inline.
                Mobile: stacks (button below) so the input can run
                full-width and the button is easy to thumb-tap. */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <div className="relative flex-1">
                <input
                  ref={urlInputRef}
                  type="text"
                  /* Password managers (Dashlane/1Password/LastPass) treat any
                     lone text field as a username slot and keep stuffing the
                     saved email in. These attrs opt the field out of autofill
                     across all of them; inputMode=url gives the right mobile
                     keyboard. Same guard as the home hero URL input. */
                  name="listing-url"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  inputMode="url"
                  data-form-type="other"
                  data-1p-ignore
                  data-lpignore="true"
                  value={url}
                  onChange={(e) => {
                    setUrl(e.target.value);
                    // Clear the invalid/unsupported URL hint as soon
                    // as the user starts editing - they may be
                    // replacing the bad URL with a supported one.
                    if (urlInvalid) setUrlInvalid(false);
                  }}
                  placeholder={t("analyze.placeholder")}
                  className={`w-full py-4 pl-5 border border-gray-200 rounded-xl text-base sm:text-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none ${loading ? "pr-5" : shot ? "pr-12" : "pr-20"}`}
                  disabled={loading}
                  onPaste={(e) => {
                    // A screenshot on the clipboard is the post itself.
                    if (e.clipboardData?.files?.length) { e.preventDefault(); void takeShot(e.clipboardData.files); }
                  }}
                  suppressHydrationWarning
                />
                {!loading && (
                  <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
                    {!shot && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={shotBusy}
                        aria-label={t("intake.image.add")}
                        title={t("intake.image.add")}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-60"
                      >
                        <ImagePlus className="h-4 w-4 text-gray-500" />
                      </button>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={(e) => { void takeShot(e.target.files); e.target.value = ""; }}
                    />
                    {url.trim() ? (
                      <button
                        type="button"
                        onClick={() => {
                          setUrl("");
                          // Re-focus so the user can paste/type the
                          // next URL without an extra tap.
                          urlInputRef.current?.focus();
                        }}
                        aria-label={t("paste.clear")}
                        title={t("paste.clear")}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-gray-100 hover:bg-gray-200 transition-colors"
                      >
                        <X className="h-4 w-4 text-gray-500" />
                      </button>
                    ) : (
                      <PasteFromClipboard onPaste={(u) => setUrl(u)} />
                    )}
                  </div>
                )}
              </div>
              {/* Desktop-only inline submit (lg+). On mobile + tablet
                  the same handler fires from the sticky bottom bar
                  below the form - mirrors /compare and /calculate so
                  the primary CTA is always within thumb reach on
                  phones instead of being trapped above the keyboard. */}
              <button
                type="submit"
                disabled={loading || shotBusy || done || (!url.trim() && !shot)}
                className="hidden lg:inline-flex w-auto px-6 py-4 bg-indigo-600 text-white rounded-xl font-semibold text-base hover:bg-indigo-700 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors shrink-0"
                suppressHydrationWarning
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {t("analyze.loading")}
                  </span>
                ) : done ? (
                  <span className="flex items-center justify-center gap-1.5">
                    <Check className="h-4 w-4" aria-hidden />
                    {t("intake.done")}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-1.5">
                    {t("analyze.button")}
                  </span>
                )}
              </button>
            </div>
            {/* The read below came from exactly this input: the way to
                the next one clears the box and puts the cursor back. */}
            {done && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={startAnother}
                  className="text-[13px] font-semibold text-gray-900 underline-offset-2 underline decoration-gray-300 hover:decoration-gray-900"
                >
                  {t("intake.another")}
                </button>
              </div>
            )}

          </div>
        </form>
        )}

        {/* Mobile / tablet sticky submit. Same handler the in-form
            desktop button calls. Mirrors /compare's pattern: at < lg
            the bar lifts above MobileTabBar (4rem + safe-area-inset);
            at sm:bottom-0 it sits flush at the viewport bottom on
            tablets where the tab bar is hidden. Only renders while
            the form itself is rendered (the !(hasOutcome && !user)
            gate above) so the bar doesn't float over the result
            page or the anonymous-outcome paywall. */}
        {/* Hidden while LOADING too: with the centered progress overlay
            on screen, a third floating layer (the disabled "Təhlil
            edilir..." bar) was pure crowding - there is nothing to
            submit mid-flight anyway. */}
        {!(hasOutcome && !user) && !loading && !result && (
          <div className="fixed left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.05)] lg:hidden sm:bottom-0 bottom-[calc(env(safe-area-inset-bottom)+4rem)] mb-0">
            <div className="max-w-3xl mx-auto px-4 py-3">
              <button
                type="button"
                onClick={(e) => handleSubmit(e as unknown as React.FormEvent)}
                disabled={loading || shotBusy || done || (!url.trim() && !shot)}
                className="w-full inline-flex items-center justify-center gap-2 py-3.5 bg-indigo-600 text-white rounded-xl font-semibold text-sm hover:bg-indigo-700 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
                suppressHydrationWarning
              >
                {loading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {t("analyze.loading")}
                  </>
                ) : done ? (
                  <>
                    <Check className="h-4 w-4" aria-hidden />
                    {t("intake.done")}
                  </>
                ) : (
                  <>
                    {t("analyze.button")}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Error card (Round 10d anatomy): plain heading that names
            the fact, one sentence of cause (tailored for unsupported
            hosts / non-listing URLs, verbatim backend message
            otherwise) + the supported-sites list, the retry as the one
            indigo action, and the "your limit wasn't spent" promise in
            quiet gray. The URL stays editable in the form right above.
            No red takeover: a bad link is a dead end, not a danger. */}
        {hasInlineError && (
          <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-5">
            <h3 className="text-[15px] font-extrabold text-gray-900">
              {t(refused ? "analyze.err.refused" : "analyze.err.title")}
            </h3>
            <p className="mt-1.5 text-sm leading-relaxed text-gray-600">
              {urlInvalid
                ? t("analyze.err.invalid").replace("{sites}", supportedSites)
                : error}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
              {/* A refusal does not change on a retry. */}
              {url.trim() && !urlInvalid && !refused && (
                <button
                  type="button"
                  onClick={() => void handleSubmit({ preventDefault() {} } as React.FormEvent)}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 transition-colors"
                >
                  {t("analyze.err.retry")}
                </button>
              )}
            </div>
            {/* Alternate route: the calculator where typed details fill
                the gap, the listings of the kind where no model values
                it, none where neither helps. A refusal read "if the link
                doesn't work, enter the details" although the link worked
                (owner, 2026-10-22). */}
            {nextStep && (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3">
                <span className="text-[13px] text-gray-500">{t(nextStep.lead)}</span>
                <Link
                  href={nextStep.href}
                  className="text-[13px] font-bold text-gray-900 underline-offset-2 underline decoration-gray-300 hover:decoration-gray-900"
                >
                  {t(nextStep.cta)}
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Rate limit / email-not-verified banner. Renders inline
            on EVERY viewport now. The previous ``hidden lg:block``
            tried to delegate mobile to the sticky bottom bar
            below, but that bar only fires for anonymous users
            (``!user && hasOutcome``) - signed-in users hitting
            the new "verify your email first" blocker got nothing
            on mobile. Banner in-content is also more obvious
            than a small sticky strip at the foot of the viewport. */}
        {/* Defer the rate-limit banner until streaming/loading
            completes - otherwise it flashes immediately on top of
            the result skeletons (a pre-flight 429 fires before the
            user has a chance to see anything else), making the page
            look broken. Once ``loading`` clears we either have a
            real result OR the rate-limit was the only outcome - in
            either case it's the right moment to surface the CTA. */}
        {rateLimitError && !loading && (
          <div className="mb-6">
            <RateLimitBanner error={rateLimitError} />
          </div>
        )}

        {/* Empty state sits INSIDE the max-w-3xl column so it lines
            up with the form above. Rendered only when we have nothing
            else to show - not during loading, not alongside a result,
            not next to an error banner. */}
        {!hasOutcome && (
          <div className="space-y-10 sm:space-y-12 mt-6">
            {/* Supported sites + no-link CTA. One horizontal strip,
                wraps to a second row on very narrow mobiles without
                breaking the layout. */}
            {/* Contrast notes:
                  Light mode text-gray-700 on white ≈ 7:1 contrast, ideal.
                  Dark mode our gray scale inverts (gray-700 → oklch 0.78)
                  on our bg-white override (0.22) gives ~5:1 - still AA
                  but perceptually flatter than the light-mode pair.
                  Explicit `dark:` overrides push text up one shade so
                  the chips and supporting copy read with the same
                  confidence in both themes. */}
            <div className="flex items-center justify-center flex-wrap gap-x-3 gap-y-2 text-sm">
              <span className="text-gray-600">
                {t("hero.nolink")}{" "}
                <Link
                  href="/calculate"
                  className="font-semibold text-gray-900 underline-offset-2 underline decoration-gray-300 hover:decoration-gray-900"
                >
                  {t("hero.nolink.cta")}
                </Link>
              </span>
            </div>

            {/* How it works + a preview of what a result looks like -
                mirrors the /compare landing so the two flows are
                consistent, and shows the analysis payoff (verdict +
                asking vs AI fair value) before a link is pasted. */}
            <div className="pt-6 sm:pt-8 border-t border-gray-200">
              <AnalyzeLanding />
            </div>

            <RecentActivity kind="analyze" />
          </div>
        )}
      </div>

      {/* Loading state - a labeled step-progress strip ABOVE the
          skeleton (bare shimmers read as "stuck"), then the skeleton
          in the wider result container so it matches the final
          ResultCard's footprint. */}
      {loading && pending === "intake" && (
        <div ref={resultRef} className="mx-auto mt-3 max-w-xl scroll-mt-24 sm:mt-8">
          <div className="rounded-2xl border border-gray-200 bg-white p-5 text-center shadow-sm">
            {/* The mark builds its house while the listing is read: the
                wait gets the brand's own motion instead of a spinner. */}
            <div className="mx-auto mb-3 flex justify-center" aria-hidden>
              <AnimatedLogoMark size={44} active loop />
            </div>
            <div className="text-[15px] font-extrabold text-gray-900">
              {shot ? t("intake.reading.image") : looksLikeLink(url.trim()) ? t("intake.reading") : t("intake.reading.text")}
            </div>
            <p className="mt-1 text-sm text-gray-500">{t("intake.reading.hint")}</p>
          </div>
        </div>
      )}

      {loading && pending === "analyze" && (
        <div ref={resultRef} className="max-w-6xl mx-auto mt-3 sm:mt-8 scroll-mt-24">
          {/* The listing being analyzed, as the island a pasted link
              shows: a deep link from history or the hero used to show
              only the skeleton (owner, 2026-10-11: "even if I select the
              listing from history, I should see it previewed"). */}
          {url.trim() && (
            <div className="mx-auto mb-4 max-w-3xl">
              <UrlPreviewCard url={url} variant="form" />
            </div>
          )}
          {/* The skeleton is the CURTAIN: dimmed, behind, promising the
              result's shape. The progress card floats viewport-centered
              above it so the user never scrolls to find the status
              (owner call: three stacked layers read as crowding). */}
          <div className="pointer-events-none opacity-40" aria-hidden>
            <ResultCardSkeleton />
          </div>
          <div className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center px-4">
            <div className="pointer-events-auto w-full max-w-xl shadow-[0_24px_64px_-24px_rgba(15,23,42,0.35)] rounded-2xl">
              <AnalysisProgress variant="analyze" />
            </div>
          </div>
        </div>
      )}

      {/* Result - wider container (max-w-6xl) so the new hero + 2-col
          deep-dive layout has room to breathe on lg+ without being
          pinched into the form column. */}
      {intake && !loading && (
        <div className="max-w-6xl mx-auto mt-3 sm:mt-8">
          <IntakeOutcomeView
            outcome={intake}
            input={url.trim()}
            onHint={(h) => void runIntake(url.trim(), "hint", shot ? [shot.dataUrl] : undefined, { ...(hints ?? {}), ...h })}
          />
        </div>
      )}

      {/* Anonymous post-quota PARTIAL result: real verdict + facts,
          numbers locked, the card itself is the signup CTA. */}
      {result?.partial && (
        <div className="mt-6 sm:mt-8">
          <PartialResultCard result={result} />
        </div>
      )}

      {result && !result.partial && (
        <div className="max-w-6xl mx-auto mt-6 sm:mt-8">
          <ResultCard
          analysis_mode={result.analysis_mode || "sale"}
          listing_expired={result.listing_expired}
          verdict={result.verdict}
          price={result.price}
          predicted_price={result.predicted_price}
          clearing_price={result.clearing_price}
          predicted_price_low={result.predicted_price_low}
          predicted_price_high={result.predicted_price_high}
          fair_low={result.fair_low}
          fair_high={result.fair_high}
          confidence_pct={result.confidence_pct}
          overpriced_pct={result.overpriced_pct}
          savings={result.savings}
          reasoning={result.reasoning}
          summary_trace_id={result.summary_trace_id}
          fact_checks={result.fact_checks}
          advisories={result.advisories}
          criticality={result.criticality}
          area_m2={result.area_m2}
          deed_area_m2={result.deed_area_m2}
          deed_conflict={result.deed_conflict ?? null}
          room_count={result.room_count}
          floor={result.floor}
          total_floors={result.total_floors}
          land_area_sot={result.land_area_sot}
          description={result.description}
          amenities={result.amenities}
          land={result.land}
          house_rent={result.house_rent}
          listing_id={result.listing_id ?? null}
          price_per_sot={result.price_per_sot}
          predicted_price_per_sot={result.predicted_price_per_sot}
          error_pct={result.error_pct}
          city_name={result.city_name}
          property_type={result.property_type}
          seller={result.seller ?? null}
          photo_intel={result.photo_intel ?? null}
          photo_intel_locked={result.photo_intel_locked ?? false}
          photo_intel_teaser_count={result.photo_intel_teaser_count ?? null}
          history_teaser_dom={result.history_teaser_dom ?? null}
          history_preview_days_left={result.history_preview_days_left ?? null}
          history_preview_ended={result.history_preview_ended ?? false}
          location_name={result.location_name}
          category_name={result.category_name}
          has_repair={result.has_repair}
          has_bill_of_sale={result.has_bill_of_sale}
          latitude={result.latitude}
          coords_approx={result.coords_approx ?? false}
          geo_precision={result.geo_precision ?? null}
          longitude={result.longitude}
          photo_url={result.photo_urls?.[0] ?? null}
          photo_urls={result.photo_urls ?? null}
          history_id={result.history_id}
          anon_id={result.anon_id}
          url={result.url}
          source={result.source}
          extraction={result.extraction}
          complex_pricing={result.complex_pricing}
          context={result.context}
          mortgage={result.mortgage}
          sticker_price={result.sticker_price}
          effective_price={result.effective_price}
          price_factors={result.price_factors}
          price_story={result.price_story ?? null}
          adjusted_predicted_price={result.adjusted_predicted_price}
          adjusted_predicted_price_low={result.adjusted_predicted_price_low}
          adjusted_predicted_price_high={result.adjusted_predicted_price_high}
          adjustments={result.adjustments}
          total_adjustment_pct={result.total_adjustment_pct}
          /* Fresh analyses pass the intent from form state - the
             analyze response doesn't round-trip it yet, so ResultCard
             would otherwise never see it. History / share paths get
             it from the record (once backend wiring lands). */
          user_intent={userIntent || null}
          history={result.history ?? null}
          history_locked={result.history_locked ?? false}
          investment={result.investment ?? null}
          renovation_uplift={result.renovation_uplift ?? null}
          onReset={evaluateAnother}
          /* Post-value trial invite: the user just SAW a full verdict -
             the highest-intent moment a free eligible account has. The
             card self-gates (eligible + verified + one shared dismissal
             key), so it shows here or on the home page, never nags; the
             result draws it only when it carries no Pro line (one Pro
             offer per result). */
          trialOffer={<TrialOfferCard />}
          ownCta={
            // Owner-loop hook: turn the analyzed flat into a tracked
            // home with every valuation input prefilled. Sale only.
            (result.analysis_mode || "sale") === "sale" ? (
              <TrackHomeCta
                property_type={result.property_type}
                area_m2={result.area_m2}
                room_count={result.room_count}
                floor={result.floor}
                total_floors={result.total_floors}
                land_area_sot={result.land_area_sot}
                location_name={result.location_name}
                latitude={result.latitude}
                longitude={result.longitude}
                category_name={result.category_name}
                has_repair={result.has_repair}
                has_bill_of_sale={result.has_bill_of_sale}
              />
            ) : undefined
          }
        />
        </div>
      )}

      {/* Lift body bottom padding so the footer clears whichever
          sticky bar is mounted: the rate-limit CTA below (anon +
          hasOutcome) OR the mobile-submit bar above the form (anyone
          while the form is rendered). Mounted whenever ANY sticky
          bar can claim the bottom edge. */}
      {((!user && hasOutcome) || !(hasOutcome && !user)) && <MobileBottomChromeOffset />}

      {/* Mobile sticky CTA for anonymous users with a RESULT on
          screen. The form is hidden in this state (#173) so without
          this bar the user has no visible path to "run another
          analysis". Mirrors the /calculate sticky-bar pattern: lock
          icon + headline + subtext + Sign-up Link.

          Deliberately gated on ``result`` (NOT hasOutcome, which
          includes ``loading``): "your free analysis has been used"
          floating over the progress view before any result exists
          read as premature and confusing. Failed analyses aren't
          charged, so the error state doesn't earn the bar either.
          Skipped when ``rateLimitError`` is set because the inline
          ``RateLimitBanner`` already carries the sign-up / verify /
          upgrade CTAs - the bottom bar would just duplicate them. */}
      {!user && result && !result.partial && !loading && !rateLimitError && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-amber-50 border-t border-amber-200 shadow-[0_-4px_12px_rgba(0,0,0,0.05)] lg:hidden sm:bottom-0 bottom-[calc(env(safe-area-inset-bottom)+4rem)]">
          {/* Compact single-row bar: the lock-in-a-square ate a third
              of the width for pure decoration (owner call). */}
          <div className="max-w-2xl mx-auto px-4 py-2.5 flex items-center gap-3">
            <p className="flex-1 min-w-0 text-[11px] leading-snug text-gray-700">
              <span className="font-semibold text-gray-900">{t("rate.freeused")}</span>{" "}
              {t("rate.signinhint")}
            </p>
            <Link
              href={registerHref}
              className="shrink-0 inline-flex items-center justify-center px-3.5 py-2 bg-indigo-600 text-white rounded-xl font-semibold text-xs hover:bg-indigo-700 transition-colors"
            >
              {t("auth.signup")}
            </Link>
          </div>
        </div>
      )}
    </main>
  );
}

/** What the error card offers after a refusal, by its reason code; a
 *  failed read (no reason) keeps "enter the details yourself". */
function refusalNextStep(reason: string | null): { lead: TranslationKey; cta: TranslationKey; href: string } | null {
  if (reason === null) return { lead: "analyze.err.manual", cta: "analyze.err.manual.cta", href: "/calculate" };
  switch (reason) {
    case "missing_floor":
    case "missing_area":
    case "missing_land_area":
    case "credit_price_unknown":
    case "no_coordinates":
      return { lead: "analyze.refused.fill", cta: "analyze.err.manual.cta", href: "/calculate" };
    case "rent_house_daily_price":
      return { lead: "analyze.refused.monthly", cta: "analyze.refused.calc", href: "/calculate" };
    case "commercial_not_valued":
      return { lead: "analyze.refused.commercial", cta: "analyze.refused.see", href: "/listings?category=commercial" };
    case "garage_not_valued":
      return { lead: "analyze.refused.garage", cta: "analyze.refused.see", href: "/listings?category=garage" };
    case "daily_rent_not_supported":
      return { lead: "analyze.refused.monthly_rentals", cta: "analyze.refused.see", href: "/listings?leased=true" };
    case "rent_house_too_large":
    case "rent_house_thin_place":
    case "rent_house_commercial":
    case "rent_house_not_supported":
      return { lead: "analyze.refused.house_rentals", cta: "analyze.refused.see", href: "/listings?leased=true&category=house" };
    default:
      return null;
  }
}

export default function AnalyzePage() {
  return (
    <>
      <Suspense fallback={
        <div className="max-w-4xl mx-auto px-4 py-10 text-center text-gray-400">
          <svg className="w-6 h-6 animate-spin mx-auto" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      }>
        <AnalyzeContent />
      </Suspense>
    </>
  );
}

/* ── Intake outcome (any link / pasted text / screenshot) ── */

type IntakeOutcome = { kind: "read"; data: IntakeAnalyzeResponse } | { kind: "none" };

/** "birev.az/elan/3-otaqli-menzil-xetai-16701": the link as people
 *  read it, without the scheme and the www. */
function linkLabel(url: string): string {
  try {
    const u = new URL(url);
    return `${u.hostname.replace(/^www\./, "")}${u.pathname === "/" ? "" : u.pathname}${u.search}`;
  } catch {
    return url;
  }
}

/** The scene beside the status line: on the right from sm, above the
 *  text on phones (the alerts page's placement). The paper tile keeps
 *  the art's own background in both themes. */
function IntakeArt({ scene }: { scene: "reading" | "unsupported" | "missing" | "none" }) {
  return (
    <span
      className="relative order-first mx-auto block h-40 w-40 shrink-0 overflow-hidden rounded-2xl bg-[rgb(250,249,246)] dark:bg-[rgb(30,36,52)] sm:order-last sm:mx-0 sm:h-36 sm:w-36"
      aria-hidden
    >
      <Illo base={`/illustrations/intake/${scene}`} className="h-full w-full scale-[1.2] object-cover" />
    </span>
  );
}

/** "{fields}" in a sentence, the fields as nodes (bold, or plain). */
function withFields(template: string, nodes: React.ReactNode): React.ReactNode {
  const at = template.indexOf("{fields}");
  if (at < 0) return template;
  return (
    <>
      {template.slice(0, at)}
      {nodes}
      {template.slice(at + "{fields}".length)}
    </>
  );
}

const PILL = "h-9 rounded-lg border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500";
const CTA = "inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700";
// Links are dark grey, underlined on hover: indigo is for the submit
// button and selected states (owner, 2026-10-29).
const QUIET_LINK = "text-[13px] font-bold text-gray-900 underline-offset-2 underline decoration-gray-300 hover:decoration-gray-900";

function IntakeOutcomeView({ outcome, input, onHint }: {
  outcome: IntakeOutcome;
  input: string;
  /** The person answered what the post did not say: read it again with that. */
  onHint: (h: IntakeHints) => void;
}) {
  const t = useT();
  const { locale } = useLocale();
  const lower = (k: TranslationKey) => t(k).toLocaleLowerCase(locale);

  if (outcome.kind === "none") {
    return (
      <div className="mx-auto max-w-3xl rounded-2xl border border-gray-200 bg-white p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <div className="min-w-0 flex-1">
            <h3 className="text-[15px] font-extrabold text-gray-900">{t("analyze.err.title")}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-gray-700">{t("intake.none")}</p>
            <p className="mt-1 text-[13px] leading-relaxed text-gray-500">{t("intake.none.hint")}</p>
            <div className="mt-3 border-t border-gray-100 pt-3">
              <Link href="/calculate?src=intake" className={QUIET_LINK}>
                {t("analyze.err.manual.cta")}
              </Link>
            </div>
          </div>
          <IntakeArt scene="none" />
        </div>
      </div>
    );
  }

  const { intake: r, status } = outcome.data;
  const f = r.fields;
  const c = intakeCase(outcome.data);
  const unknown = unknownParts(f);
  const isLink = looksLikeLink(input);
  const calcHref = calculatorHref(r, c);
  const seeHref = listingsHref(c);
  const isRent = f.deal === "rent";

  // What was read, in the card's order.
  const facts: { k: TranslationKey; v: string }[] = [];
  if (!unknown.deal) facts.push({ k: "intake.f.deal", v: t(`intake.v.${f.deal}` as TranslationKey) });
  if (!unknown.type) {
    const building = f.property_type === "apartment" && (f.building === "new" || f.building === "old")
      ? ` · ${t(f.building === "new" ? "intake.v.new" : "intake.v.old")}` : "";
    facts.push({ k: "intake.f.type", v: t(`intake.v.${f.property_type}` as TranslationKey) + building });
  }
  if (r.price_azn) facts.push({ k: "intake.f.price", v: `${formatPrice(r.price_azn)} ${isRent ? t("result.permonth") : "AZN"}` });
  const ppm = pricePerM2(r);
  if (ppm) facts.push({ k: "intake.f.ppm", v: `${formatPrice(ppm)} AZN/m²` });
  if (f.room_count) facts.push({ k: "intake.f.rooms", v: String(f.room_count) });
  if (f.area_m2) facts.push({ k: "intake.f.area", v: `${f.area_m2} m²` });
  if (f.land_area_sot) facts.push({ k: "intake.f.land", v: t("intake.v.sot").replace("{n}", String(f.land_area_sot)) });
  if (f.floor || f.total_floors) facts.push({ k: "intake.f.floor", v: `${f.floor ?? "?"}/${f.total_floors ?? "?"}` });
  const where = r.residence?.name || r.place?.name || f.location;
  if (where) facts.push({ k: "intake.f.place", v: where });
  if (f.seller) facts.push({ k: "intake.f.seller", v: t(`intake.v.${f.seller}` as TranslationKey) });
  const listedMs = listedAtMs(r);
  if (listedMs != null) facts.push({ k: "intake.f.date", v: formatMonthDay(listedMs, locale) });
  if (r.photo_count) facts.push({ k: "intake.f.photos", v: r.photo_count === 1 ? t("intake.photos.one") : t("intake.photos.n").replace("{n}", String(r.photo_count)) });
  const words = excerpt(r);

  // The missing line names the required fields; the quiet line below it
  // the rest the listing's kind states that the read did not catch (the
  // deal and the type when the card asks for them are its own lines).
  const missingKeys = c === "missing" ? r.missing : [];
  const unread = unreadFields(r).filter((k) =>
    !missingKeys.includes(k) && !(c === "ask" && (k === "deal" || k === "property_type")));
  const boldFields = (keys: string[]) => keys.map((k, i) => (
    <span key={k}>
      {i > 0 && ", "}
      <strong className="font-semibold text-gray-900">{lower(INTAKE_FIELD_LABEL[k] ?? "intake.f.place")}</strong>
    </span>
  ));

  const read = (
    <div className="rounded-2xl border border-gray-200 bg-white p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div className="min-w-0 flex-1">
          <h3 className="text-[15px] font-extrabold text-gray-900">{r.source.kind === "image" ? t("intake.read.title.image") : isLink ? t("intake.read.title") : t("intake.read.title.text")}</h3>
          {/* The link that was read, whole: a read link stays a link
              (owner, 2026-10-27). */}
          {isLink && r.source.url && (
            <a
              href={r.source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 flex min-w-0 items-center gap-1 text-xs font-semibold text-gray-900 underline-offset-2 underline decoration-gray-300 hover:decoration-gray-900"
            >
              <span className="truncate">{linkLabel(r.source.url)}</span>
              <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
            </a>
          )}
          {facts.length > 0 && (
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
              {facts.map((x) => (
                <div key={x.k}>
                  <dt className="text-xs text-gray-500">{t(x.k)}</dt>
                  <dd className="font-semibold text-gray-900 tabular-nums">{x.v}</dd>
                </div>
              ))}
            </dl>
          )}
          {words && (
            <p className="mt-3 line-clamp-3 text-[13px] leading-relaxed text-gray-500">{words}</p>
          )}

          {/* What follows from what was read. */}
          <div className="mt-3 space-y-2 text-sm leading-relaxed text-gray-700">
            {c === "valued" && (
              <p>{t(isLink ? "intake.valued.note" : "intake.valued.note.text")}</p>
            )}
            {c === "missing" && (
              <>
                <p>{withFields(t("intake.missing.line"), boldFields(missingKeys))}</p>
                <p>{t("intake.missing.next")}</p>
              </>
            )}
            {c === "ask" && unknown.deal && (
              <div>
                <p>{t("intake.ask.deal")}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(["sale", "rent"] as const).map((d) => (
                    <button key={d} type="button" onClick={() => onHint({ deal: d })} className={PILL}>
                      {t(d === "sale" ? "intake.v.sale" : "intake.v.rent")}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {c === "ask" && unknown.type && (
              <div>
                <p>{t("intake.ask.type")}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(["apartment", "house"] as const).map((k) => (
                    <button key={k} type="button" onClick={() => onHint({ property_type: k })} className={PILL}>
                      {t(k === "apartment" ? "intake.v.apartment" : "intake.v.house")}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {(c === "rent_flat" || c === "house_sale" || c === "house_rent" || c === "daily_rent" || c === "land" || c === "commercial") && (
              <p>{t(`intake.case.${c}` as TranslationKey)}</p>
            )}
            {unread.length > 0 && (
              <p className="text-[13px] text-gray-500">
                {withFields(t("intake.unread"), unread.map((k) => lower(INTAKE_FIELD_LABEL[k] ?? "intake.f.place")).join(", "))}
                {c === "valued" && (unknown.deal || unknown.type) && ` ${t("intake.assumed")}`}
              </p>
            )}
          </div>

          {/* The way on: the calculator with what was read already in
              it, or the listings of a kind we do not value yet. */}
          {status !== "valued" && (
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-gray-100 pt-3">
              {seeHref ? (
                <>
                  <Link href={seeHref} className={CTA}>{t("intake.cta.similar")}</Link>
                  {c === "daily_rent" && <Link href={calcHref} className={QUIET_LINK}>{t("intake.cta")}</Link>}
                </>
              ) : (
                <Link href={calcHref} className={CTA}>
                  {t(c === "rent_flat" ? "intake.cta.rent" : c === "house_sale" || c === "house_rent" ? "intake.cta.house" : "intake.cta")}
                </Link>
              )}
            </div>
          )}
        </div>
        <IntakeArt scene={INTAKE_ART[c]} />
      </div>
    </div>
  );

  return <div className="mx-auto max-w-3xl">{read}</div>;
}
